import React from "react";
import { Loader2, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { FeatureList } from "./FeatureList";
import type { BillingCycle } from "./BillingToggle";

interface PlanItem {
  id: number;
  name: string;
  price: number;
  trial_days: number;
  features: string[];
}

interface PricingCardProps {
  plan: PlanItem;
  cycle: BillingCycle;
  isActive: boolean;
  isFeatured: boolean;
  isSubscribing: boolean;
  onChoose: (plan: PlanItem) => void;
}

export function PricingCard({
  plan,
  cycle,
  isActive,
  isFeatured,
  isSubscribing,
  onChoose,
}: PricingCardProps) {
  // Base classes for the card container
  const baseCardClasses = "relative flex flex-col p-[28px] rounded-[24px] backdrop-blur-xl transition-all duration-500 w-full group";

  // Specific classes based on featured state
  const featuredClasses = isFeatured
    ? "bg-gradient-to-b from-[#FFFFFF] to-[#f973161f] border border-[#f97316]/30 shadow-[0_20px_40px_-15px_rgba(245,158,11,0.15)] z-10 hover:shadow-[0_25px_50px_-12px_rgba(245,158,11,0.25)]"
    : "bg-[#FFFFFF] border border-[#E2E8F0] shadow-sm hover:shadow-xl hover:border-[#CBD5E1]";

  return (
    <motion.div
      whileHover={{ scale: isFeatured ? 1.02 : 1.01, y: -4 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className={`${baseCardClasses} ${featuredClasses}`}
    >
      {/* Glow Effect on Hover for non-featured */}
      {!isFeatured && (
        <div className="absolute inset-0 rounded-[24px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none ring-1 ring-inset ring-black/5" />
      )}

      {/* Popular Ribbon for Featured */}
      {isFeatured && (
        <motion.div
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, type: "spring" }}
          className="absolute -top-4 left-1/2 -translate-x-1/2"
        >
          <div className="bg-[#f97316] text-white text-[11px] font-bold px-4 py-1.5 rounded-full shadow-sm uppercase tracking-widest whitespace-nowrap">
            Most Popular
          </div>
        </motion.div>
      )}

      {/* Card Header */}
      <div className="mb-[15px]">
        <div className="flex justify-between items-start mb-[10px]">
          <h3 className="text-xl font-bold font-['DM_Sans',sans-serif] text-[#0F172A]">
            {plan.name}
          </h3>
          {isActive && (
            <motion.span
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-[#10B981]/10 text-[#10B981] text-xs font-bold px-2.5 py-1 rounded-full shadow-sm border border-[#10B981]/20"
            >
              Current Plan
            </motion.span>
          )}
        </div>

        <div className="flex items-baseline gap-1.5 mb-2">
          <span className="text-4xl font-extrabold tracking-tight text-[#0F172A] font-['DM_Sans',sans-serif]">
            ${plan.price}
          </span>
          <span className="text-[#475569] font-medium">
            /{cycle === "yearly" ? "year" : "mo"}
          </span>
        </div>

        {/* Free Trial Badge */}
        {plan.trial_days > 0 && (
          <div className="inline-flex mt-3">
            <span className="text-xs font-semibold text-[#f97316] bg-[#ED5A29]/10 px-2.5 py-1 rounded-md border border-[#f97316]/20">
              {plan.trial_days}-day free trial
            </span>
          </div>
        )}
      </div>

      {/* Feature List Component */}
      <FeatureList features={plan.features ?? []} />

      {/* Action Button */}
      <div className="mt-[25px]">
        <button
          disabled={isActive || isSubscribing}
          onClick={() => onChoose(plan)}
          className={`relative w-full py-3.5 px-6 rounded-xl font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2 overflow-hidden group/btn ${isActive
            ? "bg-[#10B981] text-white cursor-default shadow-sm shadow-[#10B981]/20"
            : isFeatured
              ? "bg-gradient-to-r from-[#ED5A29] to-[#F97316] text-white shadow-md shadow-[#ED5A29]/25 hover:shadow-lg hover:shadow-[#ED5A29]/30"
              : "bg-white text-[#0F172A] border border-[#E2E8F0] hover:border-[#94A3B8] shadow-sm hover:bg-slate-50"
            }`}
        >
          {/* Button Hover Glow / Shine */}
          {!isActive && (
            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300 ease-in-out" />
          )}

          <span className="relative z-10 flex items-center gap-2">
            {isSubscribing && <Loader2 className="h-5 w-5 animate-spin" />}
            {isActive
              ? "Current Plan"
              : isSubscribing
                ? "Processing..."
                : "Choose Plan"}
            {!isActive && !isSubscribing && (
              <ArrowRight className={`h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1 ${isFeatured ? "text-white" : ""}`} />
            )}
          </span>
        </button>
      </div>
    </motion.div>
  );
}
